/**
 * Modal de Sincronização Manual - JurisPrazo V2
 * Permite ao usuário escolher o período para sincronização
 */

import React, { useState, useEffect } from 'react';
import { Calendar, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button, Input, Modal } from './ui';
import { SyncService } from '../services/syncService';
import { FirebaseService } from '../services/firebaseService';

const SyncModal = ({ isOpen, onClose, currentUser, onSyncComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [ultimaSincronizacao, setUltimaSincronizacao] = useState(null);
  
  // Carregar última sincronização quando modal abrir
  useEffect(() => {
    if (isOpen && currentUser?.uid && !currentUser.isDemoUserManual) {
      loadUltimaSincronizacao();
    }
  }, [isOpen, currentUser]);

  const loadUltimaSincronizacao = async () => {
    try {
      // Buscar última sincronização do Firebase (você pode implementar isso no SyncService)
      const ultimaSync = await FirebaseService.getUltimaSincronizacao?.(currentUser.uid);
      setUltimaSincronizacao(ultimaSync);
      
      // Se há uma sincronização recente (menos de 1 dia), sugerir período mais recente
      if (ultimaSync && ultimaSync.dataFim) {
        const ultimaData = new Date(ultimaSync.dataFim);
        const agora = new Date();
        const diffDias = Math.floor((agora - ultimaData) / (1000 * 60 * 60 * 24));
        
        if (diffDias < 1) {
          // Se sincronizou hoje, sugerir apenas hoje
          setFormData({
            startDate: agora.toISOString().split('T')[0],
            endDate: agora.toISOString().split('T')[0]
          });
        } else if (diffDias < 7) {
          // Se sincronizou esta semana, sugerir apenas período após última sync
          const proximoDia = new Date(ultimaData.getTime() + 24 * 60 * 60 * 1000);
          setFormData({
            startDate: proximoDia.toISOString().split('T')[0],
            endDate: agora.toISOString().split('T')[0]
          });
        }
      }
    } catch (error) {
      console.error('Erro ao carregar última sincronização:', error);
    }
  };
  
  // Estados do formulário
  const [formData, setFormData] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 dias atrás
    endDate: new Date().toISOString().split('T')[0] // hoje
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar mensagens ao editar
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  // Handler para períodos pré-definidos
  const handleQuickPeriod = (days) => {
    const hoje = new Date();
    const startDate = new Date(hoje.getTime() - (days * 24 * 60 * 60 * 1000));
    
    setFormData({
      startDate: startDate.toISOString().split('T')[0],
      endDate: hoje.toISOString().split('T')[0]
    });
    
    // Limpar mensagens
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const validateDates = () => {
    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);
    const hoje = new Date();
    
    if (startDate > endDate) {
      return 'A data de início deve ser anterior à data de fim.';
    }
    
    if (endDate > hoje) {
      return 'A data de fim não pode ser no futuro.';
    }
    
    // Verificar se o período não é muito extenso (máximo 90 dias)
    const diffDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
    if (diffDays > 90) {
      return 'O período máximo para busca é de 90 dias.';
    }
    
    return null;
  };

  const handleSync = async () => {
    const validationError = validateDates();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!currentUser?.uid || currentUser.isDemoUserManual) {
      setError('Busca de prazos disponível apenas para usuários autenticados.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Verificar se usuário tem OABs configuradas
      const oabs = await FirebaseService.getUserOABs(currentUser.uid);
      if (oabs.length === 0) {
        setError('Nenhuma OAB configurada. Vá em Configurações para adicionar suas OABs.');
        return;
      }

      // ========== ETAPA 1: BUSCAR COMUNICAÇÕES ==========
      console.log(`🔄 ETAPA 1: Buscando comunicações do PJe...`);
      console.log(`📅 Período: ${formData.startDate} a ${formData.endDate}`);
      console.log(`⚖️ OABs: ${oabs.map(oab => `${oab.numero}/${oab.seccional}`).join(', ')}`);
      
      const result = await SyncService.executeSmartSync(
        currentUser.uid, 
        oabs, 
        'manual',
        {
          startDate: formData.startDate,
          endDate: formData.endDate
        }
      );
      
      const totalComunicacoes = result.totalComunicacoes || 0;
      const novasComunicacoes = result.novasComunicacoes || 0;
      const duplicadas = result.comunicacoesDuplicadas || 0;

      // ========== ETAPA 2: PROCESSAR COM IA (AUTOMÁTICO) ==========
      let prazosDetectados = 0;
      let comunicacoesProcessadas = 0;
      
      if (novasComunicacoes > 0) {
        console.log(`🤖 ETAPA 2: Processando ${novasComunicacoes} comunicações com IA...`);
        
        // Importar serviço dinamicamente
        const { default: ComunicacaoService } = await import('../services/comunicacaoService');
        
        // Processar comunicações pendentes (máximo 15 por vez para não demorar muito)
        const resultadoIA = await ComunicacaoService.processarComunicacoesAutomaticamente(currentUser.uid);
        
        prazosDetectados = resultadoIA.prazosGerados || 0;
        comunicacoesProcessadas = resultadoIA.processadas || 0;
        
        console.log(`✅ IA processou ${comunicacoesProcessadas} comunicações, detectou ${prazosDetectados} prazos`);
      }
      
      // ========== RESULTADO FINAL ==========
      setSuccess(`✅ Busca e análise concluídas!

📊 COMUNICAÇÕES:
   • ${totalComunicacoes} encontradas no período
   • ${novasComunicacoes} novas (${duplicadas} já existiam)

🤖 ANÁLISE COM IA:
   • ${comunicacoesProcessadas} comunicações analisadas
   • ${prazosDetectados} prazos detectados

📅 Período: ${new Date(formData.startDate + 'T12:00:00').toLocaleDateString('pt-BR')} a ${new Date(formData.endDate + 'T12:00:00').toLocaleDateString('pt-BR')}`);
      
      // Notificar componente pai sobre o sucesso
      if (onSyncComplete) {
        // Limpar cache antes de atualizar
        const { default: cacheService } = await import('../services/cacheService');
        cacheService.clear();
        
        onSyncComplete({
          ...result,
          prazosDetectados,
          comunicacoesProcessadas
        });
      }
      
    } catch (error) {
      console.error('❌ Erro na busca de prazos:', error);
      setError('Erro na busca: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Buscar Prazos">
      <div className="space-y-6">
        {/* Descrição */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <RefreshCw className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900 mb-1">🚀 Buscar Prazos Processuais</h3>
              <p className="text-sm text-blue-700 mb-2">
                Escolha o período para buscar seus prazos processuais. 
              </p>
              <div className="bg-blue-100 p-3 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-1">🤖 Processamento Inteligente:</h4>
                <p className="text-xs text-blue-800">
                  • Busca comunicações do PJe automaticamente<br/>
                  • Analisa com IA para detectar prazos<br/>
                  • Cria prazos automaticamente<br/>
                  • <strong>Otimizado:</strong> Dashboard carrega apenas dados já processados
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Informação da Última Sincronização */}
        {ultimaSincronizacao && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-green-900 mb-1">✅ Última Sincronização</h4>
                <p className="text-sm text-green-700">
                  <strong>Data:</strong> {ultimaSincronizacao.dataFim ? new Date(ultimaSincronizacao.dataFim).toLocaleDateString('pt-BR') : 'N/A'}<br/>
                  <strong>Período:</strong> {ultimaSincronizacao.dataInicio ? new Date(ultimaSincronizacao.dataInicio).toLocaleDateString('pt-BR') : 'N/A'} a {ultimaSincronizacao.dataFim ? new Date(ultimaSincronizacao.dataFim).toLocaleDateString('pt-BR') : 'N/A'}<br/>
                  <strong>💡 Dica:</strong> Para evitar duplicação, sincronize apenas o período após a última busca.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Períodos Rápidos */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-600" />
            <h4 className="font-medium text-gray-900">Períodos Rápidos</h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <button
              type="button"
              onClick={() => handleQuickPeriod(1)}
              disabled={loading}
              className="px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              📅 1 dia
            </button>
            <button
              type="button"
              onClick={() => handleQuickPeriod(7)}
              disabled={loading}
              className="px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              📅 7 dias
            </button>
            <button
              type="button"
              onClick={() => handleQuickPeriod(15)}
              disabled={loading}
              className="px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              📅 15 dias
            </button>
            <button
              type="button"
              onClick={() => handleQuickPeriod(30)}
              disabled={loading}
              className="px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              📅 30 dias
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Clique em um período para preencher automaticamente as datas (de X dias atrás até hoje)
          </p>
        </div>

        {/* Formulário */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            type="date"
            label="Data de Início *"
            value={formData.startDate}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
            icon={Calendar}
            required
            disabled={loading}
          />
          <Input
            type="date"
            label="Data de Fim *"
            value={formData.endDate}
            onChange={(e) => handleInputChange('endDate', e.target.value)}
            icon={Calendar}
            required
            disabled={loading}
          />
        </div>

        {/* Informações do período */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-700">
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium">Período selecionado:</span>
              <span className="text-blue-600 font-medium">
                {Math.ceil((new Date(formData.endDate) - new Date(formData.startDate)) / (1000 * 60 * 60 * 24)) + 1} dias
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <span className="text-gray-500">De:</span>
                <span className="ml-1 font-medium">{new Date(formData.startDate + 'T12:00:00').toLocaleDateString('pt-BR')}</span>
              </div>
              <div>
                <span className="text-gray-500">Até:</span>
                <span className="ml-1 font-medium">{new Date(formData.endDate + 'T12:00:00').toLocaleDateString('pt-BR')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Mensagens de erro */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-red-900 mb-1">Erro na Busca</h4>
                <p className="text-sm text-red-700 whitespace-pre-line">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Mensagens de sucesso */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-green-900 mb-1">Busca Concluída</h4>
                <p className="text-sm text-green-700 whitespace-pre-line">{success}</p>
              </div>
            </div>
          </div>
        )}

        {/* Botões */}
        <div className="flex gap-3 pt-4">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
            className="flex-1"
          >
            {success ? 'Fechar' : 'Cancelar'}
          </Button>
          
          {!success && (
            <Button
              onClick={handleSync}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Buscando...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Buscar Prazos
                </>
              )}
            </Button>
          )}
        </div>

        {/* Dicas */}
        {!success && !error && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-sm text-yellow-800">
              <div className="font-medium mb-2">💡 Dicas:</div>
              <ul className="space-y-1 text-xs">
                <li>• Para busca inicial, recomendamos os últimos 30 dias</li>
                <li>• Períodos muito extensos podem demorar mais para processar</li>
                <li>• O sistema detectará automaticamente os prazos das comunicações</li>
                <li>• Esta busca não afeta as configurações automáticas</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SyncModal;
